import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar as CalendarIcon, Filter, TrendingUp, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { db } from '../firebase';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { createLog } from '@/utils/createLog';

export default function DeveloperPerformance() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tickets, setTickets] = useState([]);
    const [developers, setDevelopers] = useState([]);
    const [dateRange, setDateRange] = useState({
        from: new Date(new Date().setMonth(new Date().getMonth() - 1)), // Last month by default
        to: new Date()
    });

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        if (storedUser) {
            setUser(storedUser);
            const isAuthorized = storedUser.role === 'admin' || storedUser.role === 'super_admin' || storedUser.role === 'superadmin';
            if (!isAuthorized) {
                createLog({
                    ticket: { id: "N/A", ticketNumber: "N/A" },
                    action: "unauthorized_access",
                    message: `User attempted to access unauthorized page: Performance`,
                    user: storedUser,
                });
                alert("You do not have access to this page");
                navigate("/dashboard", { replace: true });
                return;
            }
            fetchData(storedUser);
        }
    }, [navigate]);

    const fetchData = async (currentUser) => {
        setLoading(true);
        try {
            // 1. Fetch Developers (Users with role 'developer')
            // Note: If users collection has roles, filter. Otherwise fetch all and filter in memory.
            // Assuming 'users' collection exists and has role field based on Tickets.js usage.
            const usersQuery = query(collection(db, 'users'), where('role', '==', 'developer'));
            const usersSnap = await getDocs(usersQuery);
            const devsList = usersSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setDevelopers(devsList);

            // 2. Fetch Tickets based on role access (similar to Reports)
            // For performace view, typically admin/manager view.
            // If current user is developer, maybe only show their own? Or show all if transparency allowed.
            // Let's stick to the same pattern: Admins see all, Devs see theirs (maybe comparative? let's stick to assigned for now), Client might not see this page or see limited.
            // Actually, "Performance" implies seeing how everyone is doing, usually an Admin feature.
            // But let's follow the standard data access pattern for now.

            let ticketsQuery;
            const ticketsRef = collection(db, 'tickets');

            if (currentUser.role === 'admin' || currentUser.role === 'super_admin') {
                ticketsQuery = query(ticketsRef, orderBy('created_at', 'desc'));
            } else if (currentUser.role === 'developer') {
                // A developer might want to see their own stats vs others? 
                // For now, let's load ALL tickets for performance to allow comparison if they have access to the page 
                // OR restrict to just them. 
                // Let's assume transparency for 'performance' tab if they can access it, or restrict access in Layout.
                // If we restrict access in Layout, we can just fetch all here.
                // If we allow dev to see, they probably only see their own tickets in 'Tickets' page, 
                // but 'Performance' implies aggregate data. 
                // Let's fetch ALL for metrics if role is admin, otherwise just theirs if we want to be strict.
                // DECISION: Fetch ALL for Admin/SuperAdmin. Fetch ONLY assigned for Developer (they see their own stats). 
                ticketsQuery = query(ticketsRef, where('assigned_developer', '==', currentUser.uid), orderBy('created_at', 'desc'));
            } else {
                // Clients usually don't see dev performance.
                ticketsQuery = query(ticketsRef, where('created_by', '==', currentUser.uid), orderBy('created_at', 'desc'));
            }

            // OVERRIDE: If we want a leaderboard, we need all tickets.
            // Let's fetch all tickets for Admin/SuperAdmin. 
            // For Developer, let's also fetch all tickets but maybe anonymize? 
            // actually, let's just stick to what they are allowed to see.
            // If a dev can only see their tickets, they can't compare.
            // Let's stick to the previous pattern: 

            const ticketsSnap = await getDocs(ticketsQuery);
            const ticketsList = ticketsSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdDate: doc.data().created_at?.toDate() || new Date()
            }));
            setTickets(ticketsList);

        } catch (error) {
            console.error("Error fetching performance data:", error);
            toast.error("Failed to load performance data");
        } finally {
            setLoading(false);
        }
    };

    const filteredTickets = useMemo(() => {
        return tickets.filter(ticket => {
            const ticketDate = ticket.createdDate;
            return (!dateRange.from || ticketDate >= dateRange.from) &&
                (!dateRange.to || ticketDate <= new Date(dateRange.to.setHours(23, 59, 59, 999)));
        });
    }, [tickets, dateRange]);

    const developerStats = useMemo(() => {
        const stats = {};

        // Initialize with fetched developers (so even those with 0 tickets show up)
        // If current user is dev, only they might show up if we filtered devsList. 
        // But we fetched ALL developers in step 1 (if admin). 
        // If current user is developer, we might want to only show them?
        // Let's use the 'developers' state which we fetched.
        // If the user is a developer, `developers` state might contain everyone (public profile) or just them? 
        // The query `where('role', '==', 'developer')` fetches all.

        developers.forEach(dev => {
            stats[dev.id] = {
                id: dev.id,
                name: dev.name || 'Unknown',
                totalAssigned: 0,
                completed: 0,
                inProgress: 0,
                open: 0,
                highPriority: 0
            };
        });

        // If a ticket is assigned to someone not in 'developers' list (e.g. deleted user), handle it?
        // Or if we are a developer user and only fetched our own tickets, we will only populate our own stats.

        filteredTickets.forEach(ticket => {
            const devId = ticket.assigned_developer;
            if (!devId) return; // Unassigned

            if (!stats[devId]) {
                // Maybe add them if not in list (e.g. if we didn't fetch them or they are not 'developer' role anymore)
                stats[devId] = {
                    id: devId,
                    name: ticket.assigned_developer_name || 'Unknown',
                    totalAssigned: 0, completed: 0, inProgress: 0, open: 0, highPriority: 0
                };
            }

            const stat = stats[devId];
            stat.totalAssigned++;

            const status = ticket.status?.toLowerCase() || '';
            if (status === 'open' || status === 'todo') stat.open++;
            else if (status === 'in progress' || status === 'inprogress') stat.inProgress++;
            else if (['closed', 'done', 'completed', 'resolved', 'deployed'].includes(status)) stat.completed++;

            if (ticket.priority === 'High') stat.highPriority++;
        });

        // Filter out developers with 0 tickets ONLY IF we want to hide inactive ones? 
        // Or keep them to show "0". Let's keep them but maybe sort by activity.
        return Object.values(stats).sort((a, b) => b.totalAssigned - a.totalAssigned);
    }, [filteredTickets, developers]);

    const chartData = useMemo(() => {
        // Prepare data for Recharts
        return developerStats.map(stat => ({
            name: stat.name.split(' ')[0], // First name for chart
            fullName: stat.name,
            Completed: stat.completed,
            InProgress: stat.inProgress,
            Open: stat.open
        })).slice(0, 10); // Show top 10 mainly
    }, [developerStats]);

    return (
        <Layout user={user}>
            <div className="space-y-6 animate-in fade-in duration-500">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-5">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Developer Performance</h1>
                        <p className="text-muted-foreground mt-1">
                            Monitor ticket distribution and completion rates
                        </p>
                    </div>
                </div>

                {/* Filters */}
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row gap-4 items-end">
                            <div className="space-y-2 flex-1">
                                <label className="text-sm font-medium">Date Range</label>
                                <div className="flex gap-2 items-center">
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !dateRange.from && "text-muted-foreground")}>
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {dateRange.from ? format(dateRange.from, "PPP") : <span>Pick a start date</span>}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={dateRange.from}
                                                onSelect={(date) => setDateRange(prev => ({ ...prev, from: date }))}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                    <span className="text-muted-foreground">-</span>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !dateRange.to && "text-muted-foreground")}>
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {dateRange.to ? format(dateRange.to, "PPP") : <span>Pick an end date</span>}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={dateRange.to}
                                                onSelect={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                            <div className="pb-0.5">
                                <Button variant="ghost" onClick={() => {
                                    setDateRange({
                                        from: new Date(new Date().setMonth(new Date().getMonth() - 1)),
                                        to: new Date()
                                    });
                                }}>
                                    Reset
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Charts */}
                <Card>
                    <CardHeader>
                        <CardTitle>Ticket Status by Developer</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[350px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip
                                        cursor={{ fill: 'transparent' }}
                                        content={({ active, payload, label }) => {
                                            if (active && payload && payload.length) {
                                                return (
                                                    <div className="rounded-lg border bg-background p-2 shadow-sm">
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className="flex flex-col">
                                                                <span className="text-[0.70rem] uppercase text-muted-foreground">
                                                                    Developer
                                                                </span>
                                                                <span className="font-bold text-muted-foreground">
                                                                    {label}
                                                                </span>
                                                            </div>
                                                            {payload.map((item) => (
                                                                <div key={item.name} className="flex flex-col">
                                                                    <span className="text-[0.70rem] uppercase text-muted-foreground">
                                                                        {item.name}
                                                                    </span>
                                                                    <span className="font-bold">
                                                                        {item.value}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Legend />
                                    <Bar dataKey="Completed" stackId="a" fill="#22c55e" radius={[0, 0, 4, 4]} />
                                    <Bar dataKey="InProgress" stackId="a" fill="#3b82f6" />
                                    <Bar dataKey="Open" stackId="a" fill="#eab308" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Detailed Table */}
                <Card>
                    <CardHeader>
                        <CardTitle>Developer Metrics</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Developer</TableHead>
                                    <TableHead>Total Assigned</TableHead>
                                    <TableHead>Completed</TableHead>
                                    <TableHead>In Progress</TableHead>
                                    <TableHead>Open</TableHead>
                                    <TableHead>High Priority</TableHead>
                                    <TableHead>Completion Rate</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {developerStats.map(stat => (
                                    <TableRow key={stat.id}>
                                        <TableCell className="font-medium">{stat.name}</TableCell>
                                        <TableCell>{stat.totalAssigned}</TableCell>
                                        <TableCell className="text-green-600 font-medium">{stat.completed}</TableCell>
                                        <TableCell className="text-blue-600">{stat.inProgress}</TableCell>
                                        <TableCell className="text-yellow-600">{stat.open}</TableCell>
                                        <TableCell className="text-red-600">{stat.highPriority}</TableCell>
                                        <TableCell>
                                            {stat.totalAssigned > 0
                                                ? Math.round((stat.completed / stat.totalAssigned) * 100)
                                                : 0}%
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {developerStats.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center h-24 text-muted-foreground">
                                            No data available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
